import { useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { printLabelFile } from "@/lib/labelPrint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };
const LABEL_STAGE = ["BOXING", "BOXED", "COMPLETED"]; // marketplace labels only exist from boxing onwards
const LABEL_MODE = ["BOXED", "COMPLETED"]; // boxing is over, the board is about labels now

// Receiver side of the marketplace labels: open a label file to print it, confirm a file's boxes as done.
// Printing is not tracked (the browser cannot know what the printer did); only the worker's confirmation counts.
export function useLabelPrinting({ workStatus, boxes, onChanged, onMessage }) {
  const { t, tServer } = useLang();
  const [busy, setBusy] = useState(false);
  const [confirmFile, setConfirmFile] = useState(null); // file row waiting for the "this completes the work" confirmation

  const fail = (e) => {
    const detail = e.response?.data?.detail;
    onMessage("error", typeof detail === "string" ? tServer(detail) : t("recv.error"));
  };

  const open = async (url) => {
    try {
      if (!(await printLabelFile(url))) onMessage("error", t("labels.popup_blocked"));
    } catch (e) {
      fail(e);
    }
  };

  // One call per box; the last box of the work answers with work_completed.
  const setApplied = async (fileBoxes, value) => {
    if (busy) return;
    setBusy(true);
    try {
      let completed = false;
      for (const box of fileBoxes) {
        if ((box.label_state === "APPLIED") === value) continue;
        const { data } = await axios.post(`${API}/boxes/${box.id}/${value ? "labels-applied" : "labels-unapplied"}`, {}, ax);
        completed = completed || !!data.work_completed;
      }
      onMessage("success", t(completed ? "labels.completed_msg" : value ? "labels.applied_msg" : "labels.unapplied_msg"));
    } catch (e) {
      fail(e);
    } finally {
      await onChanged();
      setBusy(false);
    }
  };

  // Confirming the last open file completes the work and cannot be undone from the screen, so it asks first.
  const requestApplied = (fileBoxes) => {
    const ids = new Set(fileBoxes.map(b => b.id));
    const completesWork = workStatus === "BOXED" && boxes.every(b => b.label_state === "APPLIED" || ids.has(b.id));
    if (completesWork) setConfirmFile(fileBoxes);
    else setApplied(fileBoxes, true);
  };

  return {
    stage: LABEL_STAGE.includes(workStatus),
    mode: LABEL_MODE.includes(workStatus),
    canAct: workStatus !== "COMPLETED",
    busy, open, requestApplied, confirmFile,
    cancelConfirm: () => setConfirmFile(null),
    confirmApplied: async () => { const target = confirmFile; setConfirmFile(null); await setApplied(target, true); },
    undoApplied: (fileBoxes) => setApplied(fileBoxes, false),
  };
}
